/**
 * Integração SED-SP — apoio às telas de matrícula.
 *
 * O servidor devolve as recusas no formato `throw` (lista de {type, msg, origem, codigo}).
 * Em escola SEM a integração nada disto é acionado: as telas se comportam como sempre.
 */

/** Mostra um `throw` que pode vir como texto (legado), objeto ou lista. Devolve true se havia erro. */
export function mostrarThrow (vm, dados) {

    if (!dados) return false

    if (typeof dados.erro !== 'undefined') {
        window.eventBus.$emit('alert', { msg: dados.erro, type: 'warning' })
        return true
    }

    if (typeof dados.throw === 'undefined') return false

    const t = dados.throw

    if (Array.isArray(t)) {
        vm.$alert(t.map(x => x.msg).join('<br><br>'), (t[0] && t[0].origem === 'SED') ? 'Integração com a SED' : 'Atenção!', { dangerouslyUseHTMLString: true, confirmButtonText: 'OK', customClass: 'msgbox-sed' }).catch(() => {})
    } else {
        vm.$alert((t && t.msg) ? t.msg : t, 'Atenção!', { dangerouslyUseHTMLString: true, confirmButtonText: 'OK', customClass: 'msgbox-sed' }).catch(() => {})
    }

    return true
}

/** O servidor está pedindo o tipo do cancelamento? (só acontece em matrícula vinculada à SED) */
function pedeTipoDeCancelamento (dados) {
    return dados && Array.isArray(dados.throw) && dados.throw.some(x => x.codigo === 'SED_VALIDACAO' && /tipo do cancelamento/i.test(x.msg || ''))
}

/**
 * Cancela a matrícula. Se ela estiver na SED, o servidor exige saber se o aluno NÃO COMPARECEU
 * (vira baixa na SED, reversível) ou se a matrícula foi FEITA POR ENGANO (é exclusão, feita no portal da SED).
 * Resolve com true quando o cancelamento foi concluído.
 */
export function cancelarMatricula (vm, id, motivo) {

    const url = `${vm.$store.state.config.baseUrlApi}/api/matricula/cancelarMatricula/` + id
    const enviar = (tipo) => vm.$axios.post(url, { motivo: motivo || null, sed_tipo_cancelamento: tipo || null }, vm.$store.getters.getHeaderAuth)

    return enviar(null).then(response => {

        if (!pedeTipoDeCancelamento(response.data)) {
            return !mostrarThrow(vm, response.data)
        }

        window.eventBus.$emit('loading', false)

        return vm.$confirm(
            'Esta matrícula está registrada na SED. Qual é o caso?',
            'Cancelar matrícula',
            { confirmButtonText: 'O aluno não compareceu', cancelButtonText: 'Foi matriculado por engano', distinguishCancelAndClose: true, type: 'warning' }
        ).then(() => 'NAO_COMPARECEU').catch(acao => (acao === 'cancel' ? 'ENGANO' : null)).then(tipo => {

            if (!tipo) return false

            window.eventBus.$emit('loading', true)

            return enviar(tipo).then(r => !mostrarThrow(vm, r.data))
        })
    })
}

/** O servidor está pedindo o motivo da transferência? (só em matrícula ativa na SED) */
function pedeMotivoDeTransferencia (dados) {
    return dados && Array.isArray(dados.throw) ? dados.throw.find(x => x.codigo === 'SED_PEDE_MOTIVO_TRANSFERENCIA') : null
}

/** Caixa com o motivo (lista oficial da SED, enviada pelo servidor) e a data da transferência. */
function perguntarMotivoEData (vm, motivos) {

    const h = vm.$createElement
    const hoje = vm.$moment().format('YYYY-MM-DD')
    const escolha = { motivo: '', data: hoje }

    const opcoes = [h('option', { attrs: { value: '' } }, 'Selecione…')]
        .concat(Object.keys(motivos).map(k => h('option', { attrs: { value: k } }, motivos[k])))

    const estilo = 'width: 100%; padding: 6px; margin: 4px 0 12px; border: 1px solid #dcdfe6; border-radius: 4px;'

    return vm.$msgbox({
        title: 'Transferência — dados para a SED',
        message: h('div', null, [
            h('label', null, 'Motivo da transferência'),
            h('select', { attrs: { style: estilo }, on: { change: e => { escolha.motivo = e.target.value } } }, opcoes),
            h('label', null, 'Data da transferência'),
            h('input', { attrs: { type: 'date', value: hoje, max: hoje, style: estilo }, on: { change: e => { escolha.data = e.target.value } } })
        ]),
        showCancelButton: true,
        confirmButtonText: 'Confirmar transferência',
        cancelButtonText: 'Voltar',
        beforeClose: (acao, instancia, fechar) => {
            if (acao === 'confirm' && !escolha.motivo) {
                window.eventBus.$emit('alert', { msg: 'Selecione o motivo da transferência.', type: 'warning' })
                return
            }
            fechar()
        }
    }).then(() => escolha).catch(() => null)
}

/**
 * GET de transferência. Se a matrícula estiver ativa na SED, o servidor devolve o pedido de motivo
 * (com a lista oficial); a tela pergunta motivo e data e repete a chamada.
 * Resolve com a resposta final (response) ou null se o usuário desistiu.
 */
export function transferir (vm, url) {

    return vm.$axios.get(url, vm.$store.getters.getHeaderAuth).then(response => {

        const pedido = pedeMotivoDeTransferencia(response.data)

        if (!pedido) return response

        window.eventBus.$emit('loading', false)

        return perguntarMotivoEData(vm, pedido.motivos || {}).then(escolha => {

            if (!escolha) return null

            window.eventBus.$emit('loading', true)

            const params = { sed_motivo_baixa: escolha.motivo, sed_data_baixa: escolha.data }

            return vm.$axios.get(url, Object.assign({}, vm.$store.getters.getHeaderAuth, { params }))
        })
    })
}

/**
 * Evasão / desistência de matrícula que está na SED: confirma e pergunta a DATA da ocorrência
 * (a SED registra a baixa nessa data). Matrícula fora da SED: só a confirmação de sempre.
 * Resolve com { params } para o axios, ou null se o usuário desistiu.
 */
export function confirmarComData (vm, matricula, pergunta) {

    const naSed = !!(matricula && matricula.codigo_sed)

    if (!naSed) {
        return vm.$msgbox.confirm(pergunta, 'Atenção', { confirmButtonText: 'Sim', cancelButtonText: 'Não', type: 'warning' })
            .then(() => ({ params: {} })).catch(() => null)
    }

    const h = vm.$createElement
    const hoje = vm.$moment().format('YYYY-MM-DD')
    const escolha = { data: hoje }

    return vm.$msgbox({
        title: 'Atenção',
        message: h('div', null, [
            h('p', null, pergunta),
            h('label', null, 'Data da ocorrência (vai para a SED)'),
            h('input', { attrs: { type: 'date', value: hoje, max: hoje, style: 'width: 100%; padding: 6px; margin-top: 4px; border: 1px solid #dcdfe6; border-radius: 4px;' }, on: { change: e => { escolha.data = e.target.value || hoje } } })
        ]),
        showCancelButton: true, confirmButtonText: 'Sim', cancelButtonText: 'Não', type: 'warning'
    }).then(() => ({ params: { sed_data_baixa: escolha.data } })).catch(() => null)
}
